import React from 'react';
import { PiCheckLight, PiXLight, PiWarningLight } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';

interface UnsavedChangesModalProps {
  isOpen: boolean;
  onSave: () => void;
  onDiscard: () => void;
  isLoading?: boolean;
}

const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  isOpen,
  onSave,
  onDiscard,
  isLoading = false
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md mx-4 animate-in fade-in zoom-in-95">
        {/* Icon and Title */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
            <PiWarningLight className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('Unsaved Changes')}
          </h2>
        </div>

        {/* Message */}
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-6">
          {t('You have unsaved changes in this spreadsheet. Would you like to save them before leaving?')}
        </p>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onDiscard}
            disabled={isLoading}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <PiXLight className="w-4 h-4" />
            {t('Discard Changes')}
          </button>
          <button
            onClick={onSave}
            disabled={isLoading}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <PiCheckLight className="w-4 h-4" />
            {isLoading ? t('Saving...') : t('Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnsavedChangesModal;
